import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { m } from "@/paraglide/messages";

export type Units = "metric" | "imperial";

export function UnitsToggle({
  value,
  onChange,
}: {
  value: Units;
  onChange: (u: Units) => void;
}) {
  return (
    <div className="inline-flex gap-1 rounded-lg border border-border p-1">
      {(["metric", "imperial"] as const).map((u) => (
        <button
          key={u}
          type="button"
          aria-pressed={value === u}
          onClick={() => onChange(u)}
          className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
            value === u
              ? "bg-primary text-primary-foreground"
              : "text-muted-foreground hover:text-foreground"
          }`}
        >
          {u === "metric" ? m.bmi_metric() : m.bmi_imperial()}
        </button>
      ))}
    </div>
  );
}

/** Height input: one cm field (metric) or ft + in fields (imperial). */
export function HeightInput({
  units,
  cm,
  ft,
  inch,
  onCm,
  onFt,
  onInch,
  idPrefix,
}: {
  units: Units;
  cm: string;
  ft: string;
  inch: string;
  onCm: (v: string) => void;
  onFt: (v: string) => void;
  onInch: (v: string) => void;
  idPrefix: string;
}) {
  if (units === "metric") {
    return (
      <div className="space-y-1.5">
        <Label htmlFor={`${idPrefix}-cm`}>{m.bmi_height_cm()}</Label>
        <Input
          id={`${idPrefix}-cm`}
          type="number"
          inputMode="decimal"
          value={cm}
          onChange={(e) => onCm(e.target.value)}
        />
      </div>
    );
  }
  return (
    <div className="space-y-1.5">
      <Label htmlFor={`${idPrefix}-ft`}>{m.bmi_height_ftin()}</Label>
      <div className="flex gap-2">
        <Input
          id={`${idPrefix}-ft`}
          type="number"
          inputMode="numeric"
          aria-label={m.bmi_feet()}
          placeholder={m.bmi_feet()}
          value={ft}
          onChange={(e) => onFt(e.target.value)}
        />
        <Input
          type="number"
          inputMode="numeric"
          aria-label={m.bmi_inches()}
          placeholder={m.bmi_inches()}
          value={inch}
          onChange={(e) => onInch(e.target.value)}
        />
      </div>
    </div>
  );
}

/** Weight input: kg (metric) or lb (imperial). */
export function WeightInput({
  units,
  kg,
  lb,
  onKg,
  onLb,
  idPrefix,
}: {
  units: Units;
  kg: string;
  lb: string;
  onKg: (v: string) => void;
  onLb: (v: string) => void;
  idPrefix: string;
}) {
  const metric = units === "metric";
  return (
    <div className="space-y-1.5">
      <Label htmlFor={`${idPrefix}-weight`}>
        {metric ? m.bmi_weight_kg() : m.bmi_weight_lb()}
      </Label>
      <Input
        id={`${idPrefix}-weight`}
        type="number"
        inputMode="decimal"
        value={metric ? kg : lb}
        onChange={(e) => (metric ? onKg : onLb)(e.target.value)}
      />
    </div>
  );
}
